/**
 * Attendance modal for a field event.
 */
import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { Button } from '@/components/forms/Button';
import { SegmentedControl, type SegmentOption } from '@/components/forms/SegmentedControl';
import { eventsService } from '@/services/events.service';
import {
  EVENT_ATTENDANCE_CONTROL_WIDTH,
  EVENT_ATTENDANCE_OPTIONS,
  EVENT_ATTENDANCE_SECTIONS,
} from './event-configs';
import type { EventAttendanceValue } from '@/types/event.types';
import {
  buildAttendanceUpdates,
  formatEventParticipantName,
  groupEventAttendanceParticipants,
  initialAttendanceMap,
  resolveEventOrganizerSubtitle,
} from './event-form-utils';

/** Config rows mapped to {@link SegmentedControl} options (icon + color). */
const ATTENDANCE_OPTIONS: SegmentOption<EventAttendanceValue>[] = EVENT_ATTENDANCE_OPTIONS.map(
  (option) => {
    const Icon = option.Icon;
    return {
      value: option.value,
      label: option.label,
      icon: <Icon className="h-3.5 w-3.5" />,
      color: option.color,
    };
  },
);

/** Props for {@link EventAttendanceModal}. */
interface EventAttendanceModalProps {
  eventId: string;
  onClose: () => void;
}

/** Attendance modal for a field event. */
export const EventAttendanceModal: React.FC<EventAttendanceModalProps> = ({ eventId, onClose }) => {
  const queryClient = useQueryClient();

  const { data: event, isLoading } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => eventsService.getEvent(eventId),
  });

  /** Local attendance map — not persisted until Save. */
  const [localStatus, setLocalStatus] = React.useState<Record<string, EventAttendanceValue>>({});

  React.useEffect(() => {
    if (!event) return;
    setLocalStatus(
      initialAttendanceMap(
        event.eventParticipants || [],
        event.eventUnregisteredParticipants || [],
      ),
    );
  }, [event]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const participants = event?.eventParticipants || [];
      const unregistered = event?.eventUnregisteredParticipants || [];
      await Promise.all([
        ...buildAttendanceUpdates(participants, localStatus).map((u) =>
          eventsService.updateEventParticipant(eventId, u.id, {
            attendanceStatus: u.attendanceStatus,
          } as any),
        ),
        ...buildAttendanceUpdates(unregistered, localStatus).map((u) =>
          eventsService.updateUnregisteredParticipant(eventId, u.id, {
            attendanceStatus: u.attendanceStatus,
          } as any),
        ),
      ]);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      toast.success('Présences enregistrées');
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!event) {
    return <div className="text-center text-red-500 py-8">{getErrorMessage('eventNotFound')}</div>;
  }

  const participants = event.eventParticipants || [];
  const unregistered = event.eventUnregisteredParticipants || [];
  const { organizers, students } = groupEventAttendanceParticipants(participants);

  /** One participant row: name + segmented attendance control. */
  const renderRow = (id: string, name: string, subtitle?: string) => (
    <div
      key={id}
      className="flex items-center justify-between gap-4 py-2.5 px-3 rounded-lg border border-[var(--color-1-nuance-3)] bg-[color-mix(in_srgb,var(--color-1-nuance-3)_5%,transparent)]"
    >
      <div className="min-w-0">
        <p className="text-sm font-medium truncate" style={{ color: 'var(--color-1-nuance-1)' }}>
          {name}
        </p>
        {subtitle && <p className="text-xs text-muted-foreground capitalize">{subtitle}</p>}
      </div>
      <div className="shrink-0" style={{ width: EVENT_ATTENDANCE_CONTROL_WIDTH }}>
        <SegmentedControl<EventAttendanceValue>
          size="sm"
          value={localStatus[id] ?? 'pending'}
          onChange={(v) => setLocalStatus((prev) => ({ ...prev, [id]: v }))}
          options={ATTENDANCE_OPTIONS}
          className="w-full"
        />
      </div>
    </div>
  );

  /** Rows keyed by {@link EVENT_ATTENDANCE_SECTIONS} id. */
  const rowsBySection = {
    organizers: organizers.map((p) =>
      renderRow(
        p.id,
        formatEventParticipantName(p.user?.firstName, p.user?.lastName),
        resolveEventOrganizerSubtitle(p),
      ),
    ),
    students: students.map((p) =>
      renderRow(p.id, formatEventParticipantName(p.user?.firstName, p.user?.lastName)),
    ),
    unregistered: unregistered.map((p) =>
      renderRow(
        p.id,
        formatEventParticipantName(p.firstName, p.lastName),
        p.primaryContact ?? undefined,
      ),
    ),
  };

  return (
    <div className="space-y-5">
      {EVENT_ATTENDANCE_SECTIONS.map((section) => {
        const Icon = section.Icon;
        const rows = rowsBySection[section.id];

        if (rows.length === 0) return null;

        return (
          <section key={section.id}>
            <h4
              className="text-sm font-semibold mb-2 flex items-center gap-1.5"
              style={{ color: section.accentColor }}
            >
              <Icon className="w-4 h-4" />
              {section.title}
            </h4>
            <div className="space-y-1.5">{rows}</div>
          </section>
        );
      })}

      {organizers.length === 0 && students.length === 0 && unregistered.length === 0 && (
        <p className="text-center text-muted-foreground py-8 text-sm">
          Aucun participant pour cet événement.
        </p>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-[var(--color-1-nuance-3)]">
        <Button preset="modal-cancel" onClick={onClose} disabled={saveMutation.isPending}>
          Annuler
        </Button>
        <Button
          variant="primary"
          size="md"
          icon={Save}
          iconPosition="left"
          onClick={() => saveMutation.mutate()}
          loading={saveMutation.isPending}
        >
          Enregistrer
        </Button>
      </div>
    </div>
  );
};
